import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import toast from "react-hot-toast";

const Register = () => {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [phone, setPhone] = useState("");
  const [address, setAddress] = useState("");
  const [answer, setAnswer] = useState("");
  const [loading, setLoading] = useState(false);

  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      const res = await axios.post("/api/v1/auth/register", {
        name,
        email,
        password,
        phone,
        address,
        answer,
      });
      if (res && res.data.success) {
        toast.success(res.data.message);
        navigate("/login");
      } else {
        toast.error(res.data.message);
      }
    } catch (error) {
      console.log(error);
      toast.error("Something went wrong!");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col lg:flex-row relative overflow-hidden">
      {/* LEFT SIDE - BLACK WITH CURVED EDGE */}
      <div className="w-full lg:w-1/2 bg-black relative flex items-center justify-center min-h-[30vh] lg:min-h-screen py-8 lg:py-0">
        {/* Logo Section */}
        <div className="text-white z-10 text-center px-6">
          <h1
            className="text-4xl sm:text-5xl lg:text-6xl"
            style={{ fontFamily: "'TAN PEARL', serif" }}
          >
            Russh Berry
          </h1>
          <p className="text-base sm:text-lg lg:text-xl mt-3 lg:mt-4 text-gray-400">
            Where Luxury Meets Elegance
          </p>
        </div>

        {/* Curved SVG Divider - Desktop Only */}
        <div className="hidden lg:block absolute right-0 top-0 h-full w-64 overflow-hidden">
          <svg
            className="absolute right-0 h-full"
            viewBox="0 0 400 800"
            preserveAspectRatio="none"
            style={{ transform: "translateX(50%)" }}
          >
            <path d="M 0 0 Q 200 400 0 800 L 400 800 L 400 0 Z" fill="white" />
          </svg>
        </div>

        {/* Mobile/Tablet Wave Divider */}
        <div className="lg:hidden absolute bottom-0 left-0 w-full overflow-hidden leading-none">
          <svg
            className="relative block w-full h-12 sm:h-16"
            viewBox="0 0 1200 120"
            preserveAspectRatio="none"
          >
            <path
              d="M0,0 C300,100 900,100 1200,0 L1200,120 L0,120 Z"
              fill="white"
            />
          </svg>
        </div>
      </div>

      {/* RIGHT SIDE - WHITE WITH FORM */}
      <div className="w-full lg:w-1/2 bg-white flex items-center justify-center px-6 sm:px-12 lg:px-16 py-8 lg:py-12">
        <div className="w-full max-w-md">
          {/* Sign Up Header */}
          <h2 className="text-2xl sm:text-3xl font-bold mb-6 lg:mb-8 text-gray-900">
            CREATE ACCOUNT
          </h2>

          {/* Registration Form */}
          <form onSubmit={handleSubmit} className="space-y-4 lg:space-y-5">
            {/* Name Input */}
            <div className="relative">
              <div className="absolute left-0 top-1/2 -translate-y-1/2">
                <svg
                  className="w-5 h-5 text-gray-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                  />
                </svg>
              </div>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Full Name"
                required
                className="w-full pl-8 pr-4 py-2.5 sm:py-3 border-b-2 border-gray-300 focus:border-black outline-none transition-colors bg-transparent placeholder:text-gray-400 text-base"
              />
            </div>

            {/* Email Input */}
            <div className="relative">
              <div className="absolute left-0 top-1/2 -translate-y-1/2">
                <svg
                  className="w-5 h-5 text-gray-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                  />
                </svg>
              </div>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Email Address"
                required
                className="w-full pl-8 pr-4 py-2.5 sm:py-3 border-b-2 border-gray-300 focus:border-black outline-none transition-colors bg-transparent placeholder:text-gray-400 text-base"
              />
            </div>

            {/* Password Input */}
            <div className="relative">
              <div className="absolute left-0 top-1/2 -translate-y-1/2">
                <svg
                  className="w-5 h-5 text-gray-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                  />
                </svg>
              </div>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Password"
                required
                className="w-full pl-8 pr-4 py-2.5 sm:py-3 border-b-2 border-gray-300 focus:border-black outline-none transition-colors bg-transparent placeholder:text-gray-400 text-base"
              />
            </div>

            {/* Phone Input */}
            <div className="relative">
              <div className="absolute left-0 top-1/2 -translate-y-1/2">
                <svg
                  className="w-5 h-5 text-gray-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                  />
                </svg>
              </div>
              <input
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="Phone Number"
                required
                className="w-full pl-8 pr-4 py-2.5 sm:py-3 border-b-2 border-gray-300 focus:border-black outline-none transition-colors bg-transparent placeholder:text-gray-400 text-base"
              />
            </div>

            {/* Address Input */}
            <div className="relative">
              <div className="absolute left-0 top-1/2 -translate-y-1/2">
                <svg
                  className="w-5 h-5 text-gray-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                  />
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                  />
                </svg>
              </div>
              <input
                type="text"
                value={address}
                onChange={(e) => setAddress(e.target.value)}
                placeholder="Delivery Address"
                required
                className="w-full pl-8 pr-4 py-2.5 sm:py-3 border-b-2 border-gray-300 focus:border-black outline-none transition-colors bg-transparent placeholder:text-gray-400 text-base"
              />
            </div>

            {/* Register Button */}
            <button
              type="submit"
              disabled={loading}
              className="w-full bg-black text-white py-3 sm:py-3 rounded-none font-semibold text-base sm:text-lg hover:bg-gray-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed mt-4"
            >
              {loading ? (
                <span className="flex items-center justify-center gap-2">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                  CREATING ACCOUNT...
                </span>
              ) : (
                "REGISTER"
              )}
            </button>

            {/* OR Divider */}
            <div className="relative py-2">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-300"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-4 bg-white text-gray-500">OR</span>
              </div>
            </div>

            {/* Login Link */}
            <button
              type="button"
              onClick={() => navigate("/login")}
              className="w-full bg-white border-2 border-black text-black py-3 sm:py-3 rounded-none font-semibold text-base sm:text-lg hover:bg-gray-50 transition-colors"
            >
              <span className="hidden sm:inline">
                ALREADY HAVE AN ACCOUNT? LOGIN
              </span>
              <span className="sm:hidden">ALREADY REGISTERED? LOGIN</span>
            </button>
          </form>

          {/* Terms & Privacy */}
          <p className="text-xs sm:text-sm text-gray-500 text-center mt-4 lg:mt-6 leading-relaxed px-2">
            SECURED BY WEBZARK TECHNOLOGIES
          </p>
        </div>
      </div>
    </div>
  );
};

export default Register;
